import { create } from 'zustand';
import { AuthState } from '../types/auth.types';
import * as authService from '../services/auth/authService';
import { smartCache } from '../services/cache/smartCache';
import { recentlyViewedService } from '../services/recentlyViewed/recentlyViewedService';
import { ENV } from '../config/env';

export const useAuthStore = create<AuthState>((set, _get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,

  signInWithGoogle: async () => {
    try {
      set({ isLoading: true, error: null });

      const result = await authService.signInWithGoogle();

      if (!result.success) {
        set({
          error: result.error || 'Google Sign In failed',
          isLoading: false,
        });
      }
      // Success - OAuth flow will handle the rest via redirect
      // Don't set isLoading to false here, as the redirect is in progress
    } catch (error) {
      console.error('Google Sign In error in store:', error);
      set({
        error: 'An unexpected error occurred',
        isLoading: false,
      });
    }
  },

  logout: async () => {
    try {
      set({ isLoading: true });

      await authService.logout();

      // Clear device-level caches to avoid cross-user bleed on shared devices
      try {
        await smartCache.clearAll();
      } catch {}
      try {
        await recentlyViewedService.clearRecentlyViewed();
      } catch {}

      // CRITICAL: Explicitly clear Supabase auth tokens from storage
      try {
        if (typeof localStorage !== 'undefined') {
          // Extract Supabase project ID from URL (e.g., "ugotuhirnuywkfsbvtkz" from "https://ugotuhirnuywkfsbvtkz.supabase.co")
          const supabaseProjectId = ENV.SUPABASE_URL?.match(/https:\/\/([^.]+)/)?.[1];

          if (supabaseProjectId) {
            const authTokenKey = `sb-${supabaseProjectId}-auth-token`;
            console.log('🔍 [Logout] Clearing Supabase auth token:', authTokenKey);
            localStorage.removeItem(authTokenKey);
          }

          // Clear ALL Supabase storage keys (safety net for PKCE verifiers, etc.)
          const allKeys = Object.keys(localStorage);
          const supabaseKeys = allKeys.filter(k => k.startsWith('sb-'));
          console.log('🔍 [Logout] Clearing all Supabase keys:', supabaseKeys);
          supabaseKeys.forEach(k => localStorage.removeItem(k));

          // Remove web conversation threads for all users (legacy and namespaced)
          Object.keys(localStorage)
            .filter(k => k.startsWith('tp_home_threads_v1'))
            .forEach(k => localStorage.removeItem(k));

          // Remove legacy recently viewed key if present
          localStorage.removeItem('@recently_viewed_stocks');

          console.log('✅ [Logout] Storage cleared successfully');
        }
      } catch (e) {
        console.error('Error clearing storage:', e);
      }

      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      console.error('Logout error in store:', error);
      set({ isLoading: false });
    }
  },

  checkAuth: async () => {
    try {
      set({ isLoading: true });

      const user = await authService.getCurrentUser();

      if (user) {
        set({
          user,
          isAuthenticated: true,
          isLoading: false,
        });
      } else {
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
      }
    } catch (error) {
      console.error('Check auth error in store:', error);
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });
    }
  },

  clearError: () => {
    set({ error: null });
  },
}));
